import type { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip } from '@mui/material'
import { CurrencyTextField, type Field } from '@pbt/pbt-ui-components'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import DiscountTooltip from '~/components/common/DiscountTooltip'
import { discountEditRoleBlacklist } from '~/constants/roleNames'
import { useIsChewyCheckoutEnabled } from '~/store/hooks/business'
import type { InvoiceLineItem, InvoiceLineItemPayload } from '~/types'
import { getIsRetailOrderLineItem } from '~/utils/invoiceUtils'
import useHasRole from '~/utils/useHasRole'

import { useGetIsDeclinedLineItem } from '../../utils/useDeclineOrderItem'
import ChargeDetailsFieldWrapper from './ChargeDetailsFieldWrapper'

export interface ChargeFieldDiscountAmountProps {
  className?: string
  disabled: boolean
  discountAmountField: Field
  inputClassName?: string
  item: InvoiceLineItem | RetailOrderLineItem
  markUserEditedField?: (edits: Record<string, unknown>) => void
  onEditInvoiceLineItem: (payload: InvoiceLineItemPayload) => void
}

const ChargeFieldDiscountAmount = ({
  className: classNameProp,
  disabled: disabledProp,
  discountAmountField,
  inputClassName,
  item,
  markUserEditedField,
  onEditInvoiceLineItem,
}: ChargeFieldDiscountAmountProps) => {
  const { t } = useTranslation()
  const isChewyCheckoutEnabled = useIsChewyCheckoutEnabled()
  const isRetailOrderLineItem =
    getIsRetailOrderLineItem(item) && isChewyCheckoutEnabled

  const canEditDiscount = !useHasRole(discountEditRoleBlacklist)
  const canHaveDiscount = getIsRetailOrderLineItem(item)
    ? false // RetailOrderLineItem has no concept of discountAllowed
    : item.discountAllowed

  // RetailLineOrderItems will never be allowed to have a manual discount applied here
  const disabled = isRetailOrderLineItem || !canEditDiscount || disabledProp
  const isDeclined = useGetIsDeclinedLineItem(item)

  if (isDeclined) {
    return null
  }

  return (
    <ChargeDetailsFieldWrapper
      disabled={disabled}
      label={discountAmountField.label}
    >
      {canHaveDiscount || item.discountAmount ? (
        <Tooltip
          arrow
          disableHoverListener={!disabled}
          placement="top"
          title={t('Tooltips:DISCOUNT_NOT_ALLOWED')}
        >
          <span>
            <CurrencyTextField
              noLonelyAdornment
              selectZeroOnFocus
              InputProps={{ disableUnderline: !canHaveDiscount }}
              className={classNameProp}
              disabled={disabled}
              field={{
                ...discountAmountField,
                set: (event: ChangeEvent<HTMLInputElement>) => {
                  const newValue = Number(event.target.value)
                  discountAmountField.setValue(newValue)
                  markUserEditedField?.({ discountAmount: newValue })
                  onEditInvoiceLineItem({ discountAmount: newValue })
                },
              }}
              fullWidth={false}
              // eslint-disable-next-line react/jsx-no-duplicate-props
              inputProps={{
                'aria-labelledby': 'charge-field-discount-amount',
                className: inputClassName,
              }}
              margin="none"
            />
          </span>
        </Tooltip>
      ) : (
        <DiscountTooltip />
      )}
    </ChargeDetailsFieldWrapper>
  )
}

export default ChargeFieldDiscountAmount
